// src/component-files/Forms/ProductEntryForm.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import {
    Box,
    Typography,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TextField,
    Select,
    MenuItem,
    FormControl,
    Button,
    IconButton,
    CircularProgress,
    useTheme,
} from "@mui/material";
import { AddCircleOutline, RemoveCircleOutline } from "@mui/icons-material";
import { v4 as uuidv4 } from "uuid";

const API_VENDORS = "https://figazuresql.swancreate.com/api/swa/FINANCE_Vendors";
const API_CATEGORIES = "https://figazuresql.swancreate.com/api/swa/FINANCE_Categories";
const API_SUBCATEGORIES = "https://figazuresql.swancreate.com/api/swa/FINANCE_SubCategories";
const API_UNITS = "https://figazuresql.swancreate.com/api/swa/FINANCE_UnitOfMeasure";
const API_UPLOAD = "https://media.swancreate.com/upload";
const API_PRODUCTS = "https://figazuresql.swancreate.com/api/swa/FINANCE_VendorProduct";

export default function ProductEntryForm({ onClose, onSaved }) {
    const theme = useTheme();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    // lookups & selected vendor
    const [vendors, setVendors] = useState([]);
    const [categories, setCategories] = useState([]);
    const [subcats, setSubcats] = useState([]);
    const [units, setUnits] = useState([]);
    const [vendorId, setVendorId] = useState("");

    // one �blank� row template
    const blankRow = {
        tempId: uuidv4(),
        vendorProductNumber: "",
        upcBarcode: "",
        gtin: "",
        productName: "",
        categoryId: "",
        subcategoryId: "",
        unitOfMeasureId: "",
        contractNumber: "",
        contractPrice: "",
        unitPrice: "",
        imageFile: null,
    };
    const [rows, setRows] = useState([{ ...blankRow }]);

    // fetch all lookups once
    useEffect(() => {
        Promise.all([
            axios.get(API_VENDORS),
            axios.get(API_CATEGORIES),
            axios.get(API_SUBCATEGORIES),
            axios.get(API_UNITS),
        ])
            .then(([vRes, cRes, scRes, uRes]) => {
                setVendors(vRes.data);
                setCategories(cRes.data);
                setSubcats(scRes.data);
                setUnits(uRes.data);
            })
            .catch(() => setError("Failed to load lookup data"))
            .finally(() => setLoading(false));
    }, []);

    // helper to upload a file into fig-finance folder
    const uploadFile = async file => {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("folder", "fig-finance");
        const { data } = await axios.post(API_UPLOAD, fd, {
            headers: { "Content-Type": "multipart/form-data" },
        });
        return data.imageUrl;
    };

    // add / remove / update rows
    const addRow = () =>
        setRows(prev => [...prev, { ...blankRow, tempId: uuidv4() }]);
    const removeRow = id =>
        setRows(prev => prev.filter(r => r.tempId !== id));
    const updateField = (id, field, value) =>
        setRows(prev =>
            prev.map(r =>
                r.tempId === id
                    ? {
                        ...r,
                        [field]: value,
                        ...(field === "categoryId" ? { subcategoryId: "" } : {}),
                    }
                    : r
            )
        );

    // submit all rows
    const handleSubmit = async () => {
        if (!vendorId) {
            alert("Please select a vendor");
            return;
        }
        setSaving(true);
        setError("");

        try {
            for (const row of rows) {
                let imageUrl;
                if (row.imageFile) {
                    imageUrl = await uploadFile(row.imageFile);
                }

                const payload = {
                    VendorId: vendorId,
                    VendorProductNumber: row.vendorProductNumber,
                    UPCBarcode: row.upcBarcode,
                    GTIN: row.gtin,
                    ProductName: row.productName,
                    CategoryId: row.categoryId,
                    SubcategoryID: row.subcategoryId,
                    UnitOfMeasureID: row.unitOfMeasureId || null,
                    ContractNumber: row.contractNumber,
                    ContractPrice: Number(row.contractPrice) || 0,
                    UnitPrice: Number(row.unitPrice) || 0,
                    ...(imageUrl && { ProductImageUrl: imageUrl }),
                };

                console.log("Submitting payload:", payload);
                const resp = await axios.post(API_PRODUCTS, payload);
                console.log("API response:", resp.data);
            }

            alert("Products added successfully!");
            onSaved?.();  // let parent know to refresh list
            onClose?.();  // close the form
        } catch (err) {
            console.error("Error during submission:", err);
            setError("Save failed � see console for details");
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <CircularProgress size={24} />;

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                Add Products for Vendor
            </Typography>
            {error && (
                <Typography color="error" gutterBottom>
                    {error}
                </Typography>
            )}

            <FormControl fullWidth size="small" margin="normal" required>
                <Select
                    value={vendorId}
                    displayEmpty
                    onChange={e => setVendorId(e.target.value)}
                >
                    <MenuItem value="" disabled>
                        Select Vendor
                    </MenuItem>
                    {vendors.map(v => (
                        <MenuItem key={v.VendorID} value={v.VendorID}>
                            {v.VendorName}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <Box sx={{ overflowX: "auto" }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ backgroundColor: theme.palette.background.default }}>
                            {[
                                "SKU",
                                "UPC",
                                "GTIN",
                                "Name",
                                "Category",
                                "Subcategory",
                                "Unit",
                                "Contract #",
                                "Contract Price",
                                "Unit Price",
                                "Image",
                                "",
                            ].map(hdr => (
                                <TableCell key={hdr} sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>
                                    {hdr}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row, idx) => {
                            const filteredSubs = subcats.filter(s => s.CategoryID === row.categoryId);
                            return (
                                <TableRow
                                    key={row.tempId}
                                    sx={{
                                        backgroundColor:
                                            idx % 2 === 0 ? theme.palette.action.hover : "inherit",
                                    }}
                                >
                                    <TableCell>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            value={row.vendorProductNumber}
                                            onChange={e =>
                                                updateField(row.tempId, "vendorProductNumber", e.target.value)
                                            }
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            value={row.upcBarcode}
                                            onChange={e =>
                                                updateField(row.tempId, "upcBarcode", e.target.value)
                                            }
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            value={row.gtin}
                                            onChange={e => updateField(row.tempId, "gtin", e.target.value)}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            value={row.productName}
                                            onChange={e =>
                                                updateField(row.tempId, "productName", e.target.value)
                                            }
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <FormControl fullWidth size="small">
                                            <Select
                                                value={row.categoryId}
                                                displayEmpty
                                                onChange={e =>
                                                    updateField(row.tempId, "categoryId", e.target.value)
                                                }
                                            >
                                                <MenuItem value="" disabled>
                                                    Category
                                                </MenuItem>
                                                {categories.map(c => (
                                                    <MenuItem key={c.CategoryID} value={c.CategoryID}>
                                                        {c.CategoryName}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </TableCell>
                                    <TableCell>
                                        <FormControl fullWidth size="small">
                                            <Select
                                                value={row.subcategoryId}
                                                displayEmpty
                                                disabled={!row.categoryId}
                                                onChange={e =>
                                                    updateField(row.tempId, "subcategoryId", e.target.value)
                                                }
                                            >
                                                <MenuItem value="" disabled>
                                                    Subcategory
                                                </MenuItem>
                                                {filteredSubs.map(s => (
                                                    <MenuItem key={s.SubcategoryID} value={s.SubcategoryID}>
                                                        {s.SubcategoryName}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </TableCell>
                                    <TableCell>
                                        <FormControl fullWidth size="small">
                                            <Select
                                                value={row.unitOfMeasureId}
                                                displayEmpty
                                                onChange={e =>
                                                    updateField(row.tempId, "unitOfMeasureId", e.target.value)
                                                }
                                            >
                                                <MenuItem value="">
                                                    <em>None</em>
                                                </MenuItem>
                                                {units.map(u => (
                                                    <MenuItem key={u.UnitOfMeasureID} value={u.UnitOfMeasureID}>
                                                        {u.UnitName}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            size="small"
                                            type="number"
                                            fullWidth
                                            value={row.contractNumber}
                                            onChange={e =>
                                                updateField(row.tempId, "contractNumber", e.target.value)
                                            }
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            size="small"
                                            type="number"
                                            fullWidth
                                            value={row.contractPrice}
                                            onChange={e =>
                                                updateField(row.tempId, "contractPrice", e.target.value)
                                            }
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            size="small"
                                            type="number"
                                            fullWidth
                                            value={row.unitPrice}
                                            onChange={e =>
                                                updateField(row.tempId, "unitPrice", e.target.value)
                                            }
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <input
                                            type="file"
                                            id={`file-${row.tempId}`}
                                            style={{ display: "none" }}
                                            onChange={e =>
                                                updateField(row.tempId, "imageFile", e.target.files[0])
                                            }
                                        />
                                        <label htmlFor={`file-${row.tempId}`}>
                                            <Button
                                                component="span"
                                                size="small"
                                                sx={{
                                                    backgroundColor: theme.palette.success.main,
                                                    color: "#fff",
                                                    "&:hover": { backgroundColor: theme.palette.success.dark },
                                                }}
                                            >
                                                {row.imageFile ? row.imageFile.name : "Upload"}
                                            </Button>
                                        </label>
                                    </TableCell>
                                    <TableCell>
                                        <IconButton onClick={() => removeRow(row.tempId)}>
                                            <RemoveCircleOutline />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        <TableRow>
                            <TableCell colSpan={12} align="center">
                                <IconButton onClick={addRow}>
                                    <AddCircleOutline />
                                </IconButton>
                                Add Row
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </Box>

            <Box mt={2} display="flex" justifyContent="flex-end" gap={1}>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={saving}
                >
                    {saving ? <CircularProgress size={20} /> : "Save All"}
                </Button>
            </Box>
        </Box>
    );
}
